import Microgame from '../engine/Microgame.js';

export default class MorseGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Code Morse !`, en: `Morse Code!`, es: `¡Código Morse!` },
            controls: { fr: `Appuie brièvement (. ou tape) ou maintiens enfoncé (- ou clic long)`, en: `Tap briefly (.) or hold (-)`, es: `Toca brevemente (.) o mantén presionado (-)` },
            objective: { fr: `Reproduis exactement la séquence lumineuse/sonore`, en: `Reproduce the exact light/sound sequence`, es: `Reproduce la secuencia exacta de luces/sonidos` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><circle cx="4" cy="12" r="2"/><circle cx="10" cy="12" r="2"/><path d="M16 12h4"/></svg>`,
            actions: [
                { type: 'tap', x: 50, y: 70, duration: 110 },
                { type: 'wait', duration: 160 },
                { type: 'hold', x: 50, y: 70, duration: 120, holdDuration: 320 },
                { type: 'wait', duration: 160 },
                { type: 'tap', x: 50, y: 70, duration: 110 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.sequence = []; // 'short' or 'long'
        this.currentIndex = 0;
        this.pressTime = 0;
        this.isPressing = false;
        this.bubbles = [];

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;

        // Always 3 items
        this.seqLength = 3;

        // Tighter duration
        const adjustedSpeed = 1 + (speedMult - 1) * (1.5 / 3);
        this.duration = 4000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "TAP!";

        // Generate Sequence
        const speedMult = this.engine?.speedMultiplier || 1.0;
        if (this.isPlayback) {
            this.sequence = ['short', 'long', 'short'];
        } else {
            for (let i = 0; i < this.seqLength; i++) {
                this.sequence.push(Math.random() < 0.5 ? 'short' : 'long');
            }
        }
        // At high speed, cap the number of longs so it's always completable
        const maxLongs = speedMult >= 3.7 ? 1 : speedMult >= 3.0 ? 2 : 3;
        while (this.sequence.filter(s => s === 'long').length > maxLongs) {
            const longIndices = this.sequence.map((s, i) => s === 'long' ? i : -1).filter(i => i >= 0);
            const replaceIdx = longIndices[Math.floor(Math.random() * longIndices.length)];
            this.sequence[replaceIdx] = 'short';
        }

        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.display = 'flex';
        container.style.gap = '20px';
        container.style.top = '40%';
        container.style.left = '50%';
        container.style.transform = 'translate(-50%, -50%)';
        this.container.appendChild(container);

        this.sequence.forEach((type, i) => {
            const bubble = document.createElement('div');
            bubble.style.width = type === 'short' ? '40px' : '80px';
            bubble.style.height = '40px';
            bubble.style.border = `4px solid ${this.secondaryColor}`;
            bubble.style.borderRadius = '20px';
            bubble.style.position = 'relative';
            bubble.style.overflow = 'hidden';

            // Fill element
            const fill = document.createElement('div');
            fill.style.position = 'absolute';
            fill.style.left = '0';
            fill.style.top = '0';
            fill.style.height = '100%';
            fill.style.width = '0%';
            fill.style.background = this.primaryColor;
            fill.style.transition = 'width 0.1s linear'; // Smooth fill
            bubble.appendChild(fill);

            bubble.dataset.fill = fill; // Store ref
            container.appendChild(bubble);
            this.bubbles.push({ el: bubble, fill: fill, type: type, completed: false });
        });

        // Hint text
        const hint = document.createElement('div');
        hint.style.position = 'absolute';
        hint.style.bottom = '20%';
        hint.style.width = '100%';
        hint.style.textAlign = 'center';
        hint.style.color = '#fff';
        hint.style.fontSize = '1.2rem';
        hint.textContent = "Hold for Long, Tap for Short";
        this.container.appendChild(hint);

        this.onDown = this.onDown.bind(this);
        this.onUp = this.onUp.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    update(dt) {
        if (!this.active || this.won || !this.isPressing) return;

        if (this.currentIndex < this.bubbles.length) {
            this.pressTime += dt;
            const current = this.bubbles[this.currentIndex];

            // Visual fill
            // Short: 120ms target
            // Long: 300ms target
            const targetTime = current.type === 'short' ? 120 : 300;
            const pct = Math.min(100, (this.pressTime / targetTime) * 100);
            current.fill.style.width = `${pct}%`;
        }
    }

    onDown() {
        if (!this.active || this.won) return;
        this.isPressing = true;
        this.pressTime = 0;
    }

    onUp() {
        if (!this.active || this.won || !this.isPressing) return;
        this.isPressing = false;

        if (this.currentIndex < this.bubbles.length) {
            const current = this.bubbles[this.currentIndex];
            const duration = this.pressTime;

            // Check logic - very forgiving thresholds
            let success = false;
            if (current.type === 'short') {
                if (duration < 300) success = true; // Generous: anything under 300ms is "short"
            } else {
                if (duration >= 200) success = true; // Generous: anything over 200ms is "long"
            }

            if (success) {
                current.fill.style.width = '100%';
                current.el.style.boxShadow = `0 0 15px ${this.primaryColor}`;
                this.currentIndex++;
                if (this.currentIndex >= this.bubbles.length) {
                    this.hideTimer = true;
                    this.win();
                }
            } else {
                // Fail reset
                current.fill.style.width = '0%';
                this.lose();
            }
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
